import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {Col, Form, Row} from "react-bootstrap";
import {useHistory} from "react-router-dom";
import {EntityAccountsContext} from "components/entity-accounts/context";
import {ToastContext} from "context/Toast";
import {WireTemplatesContext} from "./context";
import {SecurityContext} from "context/security";
import * as URL from "service/UrlConstant";
import {getAllErrorValuesAsString} from "utils/ErrorUtils";
import {
    getWireTemplateStepUrl,
    shouldNavigateToWireTemplateStep,
    validateRemittanceInformation,
    validateStep1, WIRE_TEMPLATE_STEP_URL
} from "./AddNewWireTemplateStep1";
import {useLocation} from "react-router";
import WireTemplateWizardLayout from "./WireTemplateWizardLayout";
import ShiftEnterPopupMessage from "components/ShiftEnterPopupMessage";
import {validateStep2} from "./AddNewWireTemplateStep2";
import {validateStep3} from "./AddNewWireTemplateStep3";
import {validateStep4} from "./AddNewWireTemplateStep4";
import {validateStep5} from "./AddNewWireTemplateStep5";
import {WIRE_TEMPLATE_NAME_INVALID_LENGTH_ERR_MSG} from "../../../utils/ConstantsMsg";

const WIRE_TEMPLATE_URL = '/admin/wire-templates';

const SECTION_TEMPLATE = 'templateInformation';
const SECTION_CREDITOR_AGENT = 'creditorAgentDetails';
const SECTION_CREDITOR = 'creditorDetails';
const SECTION_DEBTOR = 'debtorDetails';
const SECTION_INTERMEDIARY_AGENT = 'intermediaryAgentDetails';

const hasValue = (value) => value !== undefined && value !== null && value.toString().trim() !== '';

const templateFields = [
    [
        {label: 'Entity', name: 'managerId', md: 4, type: 'entity'},
        {label: 'Account', name: 'accountId', md: 4, type: 'account'},
        // Template Name is always editable, regardless of the section's Edit/Done toggle.
        {label: 'Template Name', name: 'templateName', md: 4, maxLength: 30, alwaysEditable: true, labelNote: 'RENAME AS NEEDED'},
    ],
    [
        {label: 'Settlement Currency', name: 'settlementCurrency', md: 4, type: 'currency', required: true},
        {label: 'Sender to Receiver Info', name: 'purposeCode', md: 4, maxLength: 50},
        {label: 'Sender Bank BIC', name: 'senderBIC', md: 4, maxLength: 50, alwaysDisabled: true},
    ],
    [
        {label: 'Remittance information', name: 'remittanceInformation', md: 12, maxLength: 143, type: 'textarea'},
    ],
];

const creditorAgentFields = [
    [
        {label: 'Agent BIC', name: 'creditorAgentBIC', md: 3, maxLength: 50, conditionallyRequired: true},
        {label: 'Clearing System Code', name: 'creditorAgentClearingSystemCode', md: 2, maxLength: 35, conditionallyRequired: true},
        {label: 'Clearing System ID', name: 'creditorAgentClearingSystemID', md: 4, maxLength: 35, conditionallyRequired: true},
        {label: 'Account IBAN', name: 'creditorAgentAccountIBAN', md: 3, maxLength: 50, conditionallyRequired: true},
    ],
    [
        {label: 'Creditor Agent Name', name: 'creditorAgentName', md: 4, maxLength: 255, required: true},
        {label: 'Agent Department', name: 'creditorAgentDepartment', md: 4, maxLength: 70},
        {label: 'Agent Sub Department', name: 'creditorAgentSubDepartment', md: 4, maxLength: 70},
    ],
    [
        {label: 'Street Name', name: 'creditorAgentStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'creditorAgentBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'creditorAgentBuildingName', md: 4, maxLength: 35},
        {label: 'Agent Floor', name: 'creditorAgentFloor', md: 2, maxLength: 70},
    ],
    [
        {label: 'Post Box', name: 'creditorAgentPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'creditorAgentRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'creditorAgentPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'creditorAgentTownName', md: 3, maxLength: 35, conditionallyRequired: true},
        {label: 'Town Location Name', name: 'creditorAgentTownLocationName', md: 3, maxLength: 35},
    ],
    [
        {label: 'District Name', name: 'creditorAgentDistrictName', md: 5, maxLength: 35},
        {label: 'Province', name: 'creditorAgentProvince', md: 4, maxLength: 35},
        {label: 'Country', name: 'creditorAgentCountry', md: 3, maxLength: 2, type: 'country', conditionallyRequired: true},
    ],
];

const creditorFields = [
    [
        {label: 'Creditor BIC', name: 'creditorBIC', md: 3, maxLength: 50},
        {label: 'Creditor Account IBAN', name: 'creditorAccountIBAN', md: 3, maxLength: 50, required: true},
        {label: 'Creditor Name', name: 'creditorName', md: 3, maxLength: 255, required: true},
        {label: 'Creditor Department', name: 'creditorDepartment', md: 3, maxLength: 70},
    ],
    [
        {label: 'Sub Department', name: 'creditorSubDepartment', md: 3, maxLength: 70},
        {label: 'Street Name', name: 'creditorStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'creditorBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'creditorBuildingName', md: 3, maxLength: 35},
    ],
    [
        {label: 'Floor', name: 'creditorFloor', md: 2, maxLength: 70},
        {label: 'Post Box', name: 'creditorPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'creditorRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'creditorPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'creditorTownName', md: 4, maxLength: 35,  required: true},
    ],
    [
        {label: 'Town Location Name', name: 'creditorTownLocationName', md: 4, maxLength: 35},
        {label: 'District Name', name: 'creditorDistrictName', md: 4, maxLength: 35},
        {label: 'Province', name: 'creditorProvince', md: 2, maxLength: 35},
        {label: 'Country', name: 'creditorCountry', md: 2, maxLength: 2, type: 'country',  required: true},
    ],
];

const debtorFields = [
    [
        {label: 'Debtor BIC', name: 'debtorBIC', md: 3, maxLength: 50},
        {label: 'Debtor Name', name: 'debtorName', md: 3, maxLength: 255, required: true},
        {label: 'Debtor Department', name: 'debtorDepartment', md: 3, maxLength: 70},
        {label: 'Sub Department', name: 'debtorSubDepartment', md: 3, maxLength: 70},
    ],
    [
        {label: 'Street Name', name: 'debtorStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'debtorBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'debtorBuildingName', md: 4, maxLength: 35},
        {label: 'Floor', name: 'debtorFloor', md: 2, maxLength: 70},
    ],
    [
        {label: 'Post Box', name: 'debtorPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'debtorRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'debtorPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'debtorTownName', md: 3, maxLength: 35, required: true},
        {label: 'Town Location Name', name: 'debtorTownLocationName', md: 3, maxLength: 35},
    ],
    [
        {label: 'District Name', name: 'debtorDistrictName', md: 5, maxLength: 35},
        {label: 'Province', name: 'debtorProvince', md: 4, maxLength: 35},
        {label: 'Country', name: 'debtorCountry', md: 3, maxLength: 2, type: 'country', required: true},
    ],
];

const intermediaryAgentFields = [
    [
        {label: 'Agent BIC', name: 'intermediaryAgentBIC', md: 3, maxLength: 50, conditionallyRequired: true},
        {label: 'Clearing System Code', name: 'intermediaryAgentClearingSystemCode', md: 2, maxLength: 35, conditionallyRequired: true},
        {label: 'Clearing System ID', name: 'intermediaryAgentClearingSystemID', md: 4, maxLength: 35, conditionallyRequired: true},
        {label: 'Account IBAN', name: 'intermediaryAgentAccountIBAN', md: 3, maxLength: 50},
    ],
    [
        {label: 'Agent Name', name: 'intermediaryAgentName', md: 4, maxLength: 255},
        {label: 'Agent Department', name: 'intermediaryAgentDepartment', md: 4, maxLength: 70},
        {label: 'Agent Sub Department', name: 'intermediaryAgentSubDepartment', md: 4, maxLength: 70},
    ],
    [
        {label: 'Street Name', name: 'intermediaryAgentStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'intermediaryAgentBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'intermediaryAgentBuildingName', md: 4, maxLength: 35},
        {label: 'Agent Floor', name: 'intermediaryAgentFloor', md: 2, maxLength: 70},
    ],
    [
        {label: 'Post Box', name: 'intermediaryAgentPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'intermediaryAgentRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'intermediaryAgentPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'intermediaryAgentTownName', md: 3, maxLength: 35, conditionallyRequired: true},
        {label: 'Town Location Name', name: 'intermediaryAgentTownLocationName', md: 3, maxLength: 35},
    ],
    [
        {label: 'District Name', name: 'intermediaryAgentDistrictName', md: 5, maxLength: 35},
        {label: 'Province', name: 'intermediaryAgentProvince', md: 4, maxLength: 35},
        {label: 'Country', name: 'intermediaryAgentCountry', md: 3, maxLength: 2, type: 'country', conditionallyRequired: true},
    ],
];

// Section/edit-button styling is provided by AddNewWireTemplate.css
// via the .awt-section and .awt-section__edit classes.


export default function AddNewWireTemplateStep6() {

    const history = useHistory();
    const {entityAccountsOptionsNoSwap, accountsOptionsNoSwap, setManagerDetails, setAccountDetails} = useContext(EntityAccountsContext);
    const {addWireTemplateState, setAddWireTemplateState, clearAddNewWireTemplateState, wireTemplateActionState, EDIT_ACTION,
        viewWireTemplateApproveFunction, viewWireTemplateRejectFunction} = useContext(WireTemplatesContext);
    const {makeGetRequestNoParams, makeGetRequest, makePostRequest, makePutRequest} = useContext(SecurityContext);
    const {success, error} = useContext(ToastContext);
    const [countries, setCountries] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [editingSections, setEditingSections] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const fromVar = params.get('from');
    const viewClick = ( fromVar==='viewClick' );
    const showApproveButton = viewClick && (addWireTemplateState.isValidApprover === true || addWireTemplateState.isValidApprover === 'true');
    const showRejectButton = viewClick && (addWireTemplateState.isValidRejecter === true || addWireTemplateState.isValidRejecter === 'true');

    const currentStepPage = '6'
    const backStepPage = '5'

    useEffect(() => {
        if (!viewClick && shouldNavigateToWireTemplateStep(currentStepPage, addWireTemplateState?.newPageStep)) {
            history.push(getWireTemplateStepUrl(addWireTemplateState.newPageStep))
        }
    }, [addWireTemplateState?.newPageStep, history, viewClick])

    const currentAccountOptions = useMemo(() => {
        if (accountsOptionsNoSwap && accountsOptionsNoSwap.length > 0) {
            return accountsOptionsNoSwap;
        }

        const selectedEntity = entityAccountsOptionsNoSwap.find(entity => entity.managerId?.toString() === addWireTemplateState.managerId?.toString());
        return selectedEntity?.accounts || [];
    }, [accountsOptionsNoSwap, entityAccountsOptionsNoSwap, addWireTemplateState.managerId]);

    const getSelectedManagerName = useCallback(() => {
        if (addWireTemplateState.managerName) {
            return addWireTemplateState.managerName;
        }

        const selectedEntity = entityAccountsOptionsNoSwap.find(entity => entity.managerId?.toString() === addWireTemplateState.managerId?.toString());
        return selectedEntity ? `${selectedEntity.shortName}-${selectedEntity.longName}` : '';
    }, [addWireTemplateState.managerId, addWireTemplateState.managerName, entityAccountsOptionsNoSwap]);

    const getCurrencies = useCallback(() => {
        const manager = getSelectedManagerName();

        if (!manager) {
            setCurrencies([]);
            return;
        }

        const onSuccessResponse = (res) => {
            setCurrencies(res.data || []);
        }

        const onErrorResponse = () => {}

        makeGetRequest(URL.WIRES_CURRENCY_URL, {manager}, onSuccessResponse, onErrorResponse)
    }, [getSelectedManagerName, makeGetRequest])

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = () => {}

        makeGetRequestNoParams(URL.COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    useEffect(() => {
        getCurrencies()
        getCountries()
    }, [getCurrencies, getCountries])


    const updateFormError = (fieldName) => {
        if (formErrors[fieldName]) {
            setFormErrors(previousErrors => ({...previousErrors, [fieldName]: ''}))
        }
    }

    const handleChange = (e) => {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;
        setAddWireTemplateState({...addWireTemplateState, [name]: value});

        if (name === 'remittanceInformation') {
            const remittanceError = validateRemittanceInformation(value);
            setFormErrors(previousErrors => ({...previousErrors, remittanceInformation: remittanceError}));
            return;
        }

        updateFormError(name);
        if (name === 'intermediaryAgentName') {
            setFormErrors(validateSubmit());
        }
    }

    const handleEntityChange = (e) => {
        const managerId = e.currentTarget.value;
        const selectedEntity = entityAccountsOptionsNoSwap.find(entity => entity.managerId?.toString() === managerId?.toString());
        const managerName = selectedEntity ? `${selectedEntity.shortName}-${selectedEntity.longName}` : '';

        setManagerDetails(managerId, managerName,true);
        setAddWireTemplateState({
            ...addWireTemplateState,
            managerId,
            managerName,
            accountId: '',
            accountCode: '',
            accountName: ''
        });
        updateFormError('managerId');
    }

    const handleAccountChange = (e) => {
        const accountId = e.currentTarget.value;
        const accountToSelect = currentAccountOptions.find(account => account.accountId?.toString() === accountId?.toString());

        if (accountToSelect) {
            const accountDisplayName = `${accountToSelect.accountCode}-${accountToSelect.name}`;
            setAccountDetails(accountToSelect.accountId, accountToSelect.name, accountToSelect.accountCode,true);
            setAddWireTemplateState({
                ...addWireTemplateState,
                accountId,
                accountCode: accountToSelect.accountCode,
                accountName: accountToSelect.nameShort || accountToSelect.name,
                accountDisplayName
            });
        } else {
            setAccountDetails('', '', null,true);
            setAddWireTemplateState({
                ...addWireTemplateState,
                accountId: '',
                accountCode: '',
                accountName: '',
                accountDisplayName: ''
            });
        }
        updateFormError('accountId');
    }

    const toggleSectionEdit = (sectionId) => {
        setEditingSections(previousState => ({...previousState, [sectionId]: !previousState[sectionId]}));
    }

    const validateStep6 = (addWireTemplateState) => {
        let errors = {}
        const {templateName} = addWireTemplateState;

        // Template Name validation, last page validation, template name can not be empty
        if (!templateName || templateName.trim() === '') {
            errors['templateName'] = 'Please enter template name'
        } else if (templateName.trim().length > 30) {
            errors['templateName'] = WIRE_TEMPLATE_NAME_INVALID_LENGTH_ERR_MSG
        }
        return errors
    }

    const validateSubmit = () => {
        return {
            ...validateStep1({...addWireTemplateState}),
            ...validateStep2({...addWireTemplateState}),
            ...validateStep3({...addWireTemplateState}),
            ...validateStep4({...addWireTemplateState}),
            ...validateStep5({...addWireTemplateState}),
            ...validateStep6({...addWireTemplateState}),
        };
    }

    const hasCreditorAgentErrors = (errors) => creditorAgentFields
        .flat()
        .some(field => !!errors[field.name]);

    const hasTemplateErrors = (errors) => templateFields
        .flat()
        .some(field => !!errors[field.name]);

    const hasCreditorErrors = (errors) => creditorFields
        .flat()
        .some(field => !!errors[field.name]);

    const hasDebtorErrors = (errors) => debtorFields
        .flat()
        .some(field => !!errors[field.name]);

    const hasIntermediaryAgentErrors = (errors) => intermediaryAgentFields
        .flat()
        .some(field => !!errors[field.name]);

    const buildSubmitParams = () => ({
        ...buildSubmitParamsStep1(),
        ...buildSubmitParamsStep2(),
        ...buildSubmitParamsStep3(),
        ...buildSubmitParamsStep4(),
        ...buildSubmitParamsStep5(),
    })


    // main page
    const buildSubmitParamsStep1 = () => ({
        id: addWireTemplateState.id,
        idTemplate: addWireTemplateState.idTemplate,
        idManager: addWireTemplateState.managerId,
        idAccount: addWireTemplateState.accountId,
        templateName: addWireTemplateState.templateName,
        settlementCurrency: addWireTemplateState.settlementCurrency,
        instructedCurrency: addWireTemplateState.settlementCurrency,
        purposeCode: addWireTemplateState.purposeCode,
        chargesBearer: addWireTemplateState.chargesBearer,
        senderBIC: addWireTemplateState.senderBIC,
        remittanceInformation: addWireTemplateState.remittanceInformation,
    })

    // creditor agent page
    const buildSubmitParamsStep2 = () => ({
        creditorAgentBIC:addWireTemplateState.creditorAgentBIC,
        creditorAgentClearingSystemCode:addWireTemplateState.creditorAgentClearingSystemCode,
        creditorAgentClearingSystemID:addWireTemplateState.creditorAgentClearingSystemID,
        creditorAgentAccountIBAN:addWireTemplateState.creditorAgentAccountIBAN,
        creditorAgentName:addWireTemplateState.creditorAgentName,
        creditorAgentDepartment:addWireTemplateState.creditorAgentDepartment,
        creditorAgentSubDepartment:addWireTemplateState.creditorAgentSubDepartment,
        creditorAgentStreetName:addWireTemplateState.creditorAgentStreetName,
        creditorAgentBuildingNumber:addWireTemplateState.creditorAgentBuildingNumber,
        creditorAgentBuildingName:addWireTemplateState.creditorAgentBuildingName,
        creditorAgentFloor:addWireTemplateState.creditorAgentFloor,
        creditorAgentPostBox:addWireTemplateState.creditorAgentPostBox,
        creditorAgentRoom:addWireTemplateState.creditorAgentRoom,
        creditorAgentPostCode:addWireTemplateState.creditorAgentPostCode,
        creditorAgentTownName:addWireTemplateState.creditorAgentTownName,
        creditorAgentTownLocationName:addWireTemplateState.creditorAgentTownLocationName,
        creditorAgentDistrictName:addWireTemplateState.creditorAgentDistrictName,
        creditorAgentProvince:addWireTemplateState.creditorAgentProvince,
        creditorAgentCountry:addWireTemplateState.creditorAgentCountry,
    })

    // creditor  page
    const buildSubmitParamsStep3 = () => ({
        creditorBIC:addWireTemplateState.creditorBIC,
        creditorAccountIBAN:addWireTemplateState.creditorAccountIBAN,
        creditorName:addWireTemplateState.creditorName,
        creditorDepartment:addWireTemplateState.creditorDepartment,
        creditorSubDepartment:addWireTemplateState.creditorSubDepartment,
        creditorStreetName:addWireTemplateState.creditorStreetName,
        creditorBuildingNumber:addWireTemplateState.creditorBuildingNumber,
        creditorBuildingName:addWireTemplateState.creditorBuildingName,
        creditorFloor:addWireTemplateState.creditorFloor,
        creditorPostBox:addWireTemplateState.creditorPostBox,
        creditorRoom:addWireTemplateState.creditorRoom,
        creditorPostCode:addWireTemplateState.creditorPostCode,
        creditorTownName:addWireTemplateState.creditorTownName,
        creditorTownLocationName:addWireTemplateState.creditorTownLocationName,
        creditorDistrictName:addWireTemplateState.creditorDistrictName,
        creditorProvince:addWireTemplateState.creditorProvince,
        creditorCountry:addWireTemplateState.creditorCountry,
    })

    // debtor   page
    const buildSubmitParamsStep4 = () => ({
        debtorName:addWireTemplateState.debtorName,
        debtorBIC:addWireTemplateState.debtorBIC,
        debtorDepartment:addWireTemplateState.debtorDepartment,
        debtorSubDepartment:addWireTemplateState.debtorSubDepartment,
        debtorStreetName:addWireTemplateState.debtorStreetName,
        debtorBuildingNumber:addWireTemplateState.debtorBuildingNumber,
        debtorBuildingName:addWireTemplateState.debtorBuildingName,
        debtorFloor:addWireTemplateState.debtorFloor,
        debtorPostBox:addWireTemplateState.debtorPostBox,
        debtorRoom:addWireTemplateState.debtorRoom,
        debtorPostCode:addWireTemplateState.debtorPostCode,
        debtorTownName:addWireTemplateState.debtorTownName,
        debtorTownLocationName:addWireTemplateState.debtorTownLocationName,
        debtorDistrictName:addWireTemplateState.debtorDistrictName,
        debtorProvince:addWireTemplateState.debtorProvince,
        debtorCountry:addWireTemplateState.debtorCountry,
    })
    // intermediary agent page
    const buildSubmitParamsStep5 = () => ({
        intermediaryAgentBIC: addWireTemplateState.intermediaryAgentBIC,
        intermediaryAgentClearingSystemCode: addWireTemplateState.intermediaryAgentClearingSystemCode,
        intermediaryAgentClearingSystemID: addWireTemplateState.intermediaryAgentClearingSystemID,
        intermediaryAgentAccountIBAN: addWireTemplateState.intermediaryAgentAccountIBAN,
        intermediaryAgentName: addWireTemplateState.intermediaryAgentName,
        intermediaryAgentDepartment: addWireTemplateState.intermediaryAgentDepartment,
        intermediaryAgentSubDepartment: addWireTemplateState.intermediaryAgentSubDepartment,
        intermediaryAgentStreetName: addWireTemplateState.intermediaryAgentStreetName,
        intermediaryAgentBuildingNumber: addWireTemplateState.intermediaryAgentBuildingNumber,
        intermediaryAgentBuildingName: addWireTemplateState.intermediaryAgentBuildingName,
        intermediaryAgentFloor: addWireTemplateState.intermediaryAgentFloor,
        intermediaryAgentPostBox: addWireTemplateState.intermediaryAgentPostBox,
        intermediaryAgentRoom: addWireTemplateState.intermediaryAgentRoom,
        intermediaryAgentPostCode: addWireTemplateState.intermediaryAgentPostCode,
        intermediaryAgentTownName: addWireTemplateState.intermediaryAgentTownName,
        intermediaryAgentTownLocationName: addWireTemplateState.intermediaryAgentTownLocationName,
        intermediaryAgentDistrictName: addWireTemplateState.intermediaryAgentDistrictName,
        intermediaryAgentProvince: addWireTemplateState.intermediaryAgentProvince,
        intermediaryAgentCountry: addWireTemplateState.intermediaryAgentCountry,
    })

    const handleBack = (e) => {
        e.preventDefault();
        if (viewClick) {
            history.push(WIRE_TEMPLATE_URL);
        } else {
            const newAddWireTemplateState = {...addWireTemplateState}
            newAddWireTemplateState['newPageStep']=backStepPage
            setAddWireTemplateState(newAddWireTemplateState)
            history.push(WIRE_TEMPLATE_STEP_URL + backStepPage);
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();

        const errors = validateSubmit();
        if (Object.keys(errors).length !== 0) {
            setFormErrors(errors);
            setEditingSections(previousState => ({
                ...previousState,
                [SECTION_TEMPLATE]: hasTemplateErrors(errors),
                [SECTION_CREDITOR_AGENT]: hasCreditorAgentErrors(errors),
                [SECTION_CREDITOR]: hasCreditorErrors(errors),
                [SECTION_DEBTOR]: hasDebtorErrors(errors),
                [SECTION_INTERMEDIARY_AGENT]: hasIntermediaryAgentErrors(errors),
            }));
            return
        }

        const onSuccess = () => {
            success(wireTemplateActionState.action === EDIT_ACTION ? "Template Updated Successfully!!" : "Template Created Successfully!!")
            clearAddNewWireTemplateState();
            history.push(WIRE_TEMPLATE_URL);
        }

        const onError = (err) => {
            let errorMsg = getAllErrorValuesAsString(err.response?.data || {})
            console.error('Error when submitting wire template - ', errorMsg);
            error(wireTemplateActionState.action === EDIT_ACTION ? "Error when updating template" : "Error when adding new template")
        }

        makePostRequest(
            buildSubmitParams(),
            wireTemplateActionState.action === EDIT_ACTION ? URL.WIRE_TEMPLATE_LIST_UPD_URL : URL.WIRE_TEMPLATE_CREATE_URL,
            onSuccess,
            onError
        );
    }

    const buildApprovalParams = (comment = '') => ([{
        id: addWireTemplateState.id,
        idTemplate: addWireTemplateState.idTemplate,
        comment
    }])

    const handleApprovalAction = (e, action) => {
        e.preventDefault();

        const isApproveAction = action === 'approve';
        const setProcessing = isApproveAction ? setApproving : setRejecting;
        const actionFunction = isApproveAction ? viewWireTemplateApproveFunction : viewWireTemplateRejectFunction;
        const actionUrl = isApproveAction ? URL.WIRE_TEMPLATE_LIST_APPR_URL : URL.WIRE_TEMPLATE_LIST_REJ_URL;
        const successMessage = isApproveAction ? 'Template Approved Successfully!!' : 'Template Rejected Successfully!!';
        const errorMessage = isApproveAction ? 'Error when approving template' : 'Error when rejecting template';
        const selectedTemplate = [{...addWireTemplateState}];

        setProcessing(true);

        const onSuccess = () => {
            setProcessing(false);
            success(successMessage);
            history.push(WIRE_TEMPLATE_URL);
        }

        const onError = (err) => {
            setProcessing(false);
            let errorMsg = getAllErrorValuesAsString(err.response?.data || {})
            console.error(`${errorMessage} - `, errorMsg);
            error(errorMessage)
        }

        if (actionFunction) {
            actionFunction('', selectedTemplate, onSuccess, onError);
            return;
        }

        makePutRequest(buildApprovalParams(), actionUrl, onSuccess, onError);
    }

    const renderOptions = (field) => {
        if (field.type === 'country') {
            return countries.map(country => <option key={country.countryCode} value={country.countryCode}>{country.country}</option>)
        }

        if (field.type === 'currency') {
            return currencies.map(item => {
                const currencyCode = item.currencyCode || item.isoCurrencyCode;
                if (!currencyCode) {
                    return null;
                }

                const label = item.currencyName ? `${currencyCode} - ${item.currencyName}` : currencyCode;
                return <option key={currencyCode} value={currencyCode}>{label}</option>;
            })
        }


        if (field.type === 'entity') {
            return entityAccountsOptionsNoSwap.map(entity => (
                <option key={entity.managerId} value={entity.managerId}>{entity.shortName}-{entity.longName}</option>
            ))
        }

        if (field.type === 'account') {
            return currentAccountOptions.map(account => (
                <option key={account.accountId} value={account.accountId}>{account.accountCode}-{account.name}</option>
            ))
        }

        return null
    }

    const renderMissingCurrentOption = (field) => {
        const value = addWireTemplateState[field.name] || '';
        if (!value) {
            return null;
        }

        if (field.type === 'entity' && !entityAccountsOptionsNoSwap.some(entity => entity.managerId?.toString() === value?.toString())) {
            return <option value={value}>{addWireTemplateState.managerName || value}</option>
        }

        if (field.type === 'account' && !currentAccountOptions.some(account => account.accountId?.toString() === value?.toString())) {
            const accountLabel = addWireTemplateState.accountCode ? `${addWireTemplateState.accountCode}-${addWireTemplateState.accountName || ''}` : value;
            return <option value={value}>{accountLabel}</option>
        }

        return null;
    }

    const renderField = (field, sectionId) => {
        const isEditing = !!editingSections[sectionId];
        // Fields marked alwaysEditable (e.g. Template Name) ignore the section's Edit/Done
        // toggle and remain editable at all times; alwaysDisabled still takes priority.
        const isDisabled = field.alwaysDisabled || (!field.alwaysEditable && !isEditing);
        const isSelect = ['country', 'currency', 'entity', 'account'].includes(field.type);
        const onChange = field.type === 'entity' ? handleEntityChange : (field.type === 'account' ? handleAccountChange : handleChange);
        const showConditionalMarker = sectionId !== SECTION_INTERMEDIARY_AGENT || hasValue(addWireTemplateState.intermediaryAgentName);
        const requiredMarker = field.required
            ? <span className='red-cell'>*</span>
            : (field.conditionallyRequired && showConditionalMarker ? <span className='grey-cell'>*</span> : null);

        return (
            <Col md={field.md} key={field.name}>
                <Form.Group controlId={`formWireTemplateStep7${field.name}`} className={isSelect ? 'td-select' : ''}>
                    <Form.Label column={false} className="filter-input-label">{field.label} {field.labelNote && <span className="label-note" style={{fontSize: '16px', fontWeight: 800}}>({field.labelNote})</span>} {requiredMarker}</Form.Label>
                    {field.type === 'textarea' ?
                        <>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                name={field.name}
                                value={addWireTemplateState[field.name] || ''}
                                maxLength={field.maxLength}
                                placeholder="Leave a comment"
                                readOnly={isDisabled}
                                disabled={field.alwaysDisabled}
                                onChange={handleChange}
                                isInvalid={!!formErrors[field.name]}
                            />
                            <div className="awt-helper">
                                <span>Optional(Max 4 lines of data,max 35 characters per line)</span>
                                <span className="awt-helper__count">{(addWireTemplateState[field.name] || '').length} / {field.maxLength}</span>
                            </div>
                        </> :
                        (isSelect ?
                            <Form.Control
                                as="select"
                                name={field.name}
                                value={addWireTemplateState[field.name] || ''}
                                onChange={onChange}
                                disabled={isDisabled}
                                isInvalid={!!formErrors[field.name]}>
                                <option value={''}>Select</option>
                                {renderMissingCurrentOption(field)}
                                {renderOptions(field)}
                            </Form.Control> :
                            <Form.Control
                                name={field.name}
                                value={addWireTemplateState[field.name] || ''}
                                maxLength={field.maxLength}
                                readOnly={isDisabled}
                                disabled={field.alwaysDisabled}
                                onChange={handleChange}
                                isInvalid={!!formErrors[field.name]}
                            />
                        )
                    }
                    <Form.Control.Feedback type={'invalid'}>
                        <b><i className="bi bi-exclamation-circle"></i>{formErrors[field.name]}</b>
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
        )
    }

    const renderSection = (sectionId, title, fieldRows) => (
        <section className="awt-section">
            <div className="awt-section__head">
                <h3 className="awt-section__title">{title}</h3>
                {!viewClick && (
                    <button type="button" className="btn td-btn-secondary-clear awt-section__edit" onClick={() => toggleSectionEdit(sectionId)}>
                        {editingSections[sectionId] ? 'Done' : 'Edit'}
                    </button>
                )}
            </div>
            {fieldRows.map((fieldRow, index) => (
                <Row key={index}>
                    {fieldRow.map(field => renderField(field, sectionId))}
                </Row>
            ))}
        </section>
    )

    return (
        <WireTemplateWizardLayout
            currentStep={6}
            stepTitle={viewClick ? 'Wire Template Summary' : 'Summary'}
            showCounter={!viewClick}
        >
            <ShiftEnterPopupMessage />
            <Form>
                {renderSection(SECTION_TEMPLATE, 'Template Information', templateFields)}
                {renderSection(SECTION_CREDITOR_AGENT, 'Creditor Agent Details', creditorAgentFields)}
                {renderSection(SECTION_CREDITOR, 'Creditor Details', creditorFields)}
                {renderSection(SECTION_DEBTOR, 'Debtor Details', debtorFields)}
                {renderSection(SECTION_INTERMEDIARY_AGENT, 'Intermediary Agent Details', intermediaryAgentFields)}

                <div className="awt-actions">
                    {!viewClick && (
                        <>
                            <button className="btn td-btn-secondary-clear" onClick={handleBack}>Back</button>
                            <button className="btn td-btn-primary-light" onClick={handleSubmit}>Submit</button>
                        </>
                    )}
                    {viewClick && (
                        <>
                            <button className="btn td-btn-secondary-clear" onClick={handleBack}>Back</button>
                            {showApproveButton && (
                                <button type="button" className="btn td-btn-primary-light" disabled={approving || rejecting} onClick={(e) => handleApprovalAction(e, 'approve')}>Approve</button>
                            )}
                            {showRejectButton && (
                                <button type="button" className="btn td-btn-secondary-clear" disabled={approving || rejecting} onClick={(e) => handleApprovalAction(e, 'reject')}>Reject</button>
                            )}
                        </>
                    )}
                </div>
            </Form>
        </WireTemplateWizardLayout>
    )
}


